import { useEffect } from "react";
import { useParams, Navigate, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SUPPORTED_LANGUAGES = ["en", "fr"] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

function isSupportedLanguage(lang: string): lang is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
}

/**
 * Wrapper route component that reads the `/:lang` parameter from the URL,
 * validates it against the supported languages, and synchronises the
 * i18next language accordingly.
 *
 * If the language parameter is missing or invalid, the user is redirected
 * to the default language (read from localStorage → browser preference → "fr").
 *
 * Usage in the router configuration:
 * ```tsx
 * <Route path="/:lang" element={<LanguageRoute />}>
 *   <Route index element={<Home />} />
 *   <Route path="catalog" element={<Catalog />} />
 *   ...
 * </Route>
 * ```
 */
export default function LanguageRoute() {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const location = useLocation();

  // Determine the best default language when the URL param is missing or invalid
  const getDefaultLanguage = (): SupportedLanguage => {
    // 1. Check localStorage for a previously saved preference
    try {
      const stored = localStorage.getItem("katalog-language");
      if (stored && isSupportedLanguage(stored)) {
        return stored;
      }
    } catch {
      // localStorage may be unavailable
    }

    // 2. Check the current i18next language (may come from browser detection)
    if (i18n.language && isSupportedLanguage(i18n.language)) {
      return i18n.language;
    }

    // 3. Check the raw browser language
    if (typeof navigator !== "undefined") {
      const browserLang = navigator.language?.split("-")[0];
      if (browserLang && isSupportedLanguage(browserLang)) {
        return browserLang;
      }
    }

    // 4. Fallback to French (primary language of the application)
    return "fr";
  };

  // If the lang param is not a supported language, redirect
  if (!lang || !isSupportedLanguage(lang)) {
    const defaultLang = getDefaultLanguage();

    // Build the redirect path, preserving the rest of the URL after the
    // invalid or missing language segment.
    let redirectPath: string;

    if (lang && !isSupportedLanguage(lang)) {
      // The first path segment exists but is not a valid language —
      // treat it as part of the path (e.g., /catalog → /fr/catalog)
      redirectPath = `/${defaultLang}${location.pathname}${location.search}${location.hash}`;
    } else {
      // No lang segment at all — just prepend the default language
      const restOfPath = location.pathname === "/" ? "" : location.pathname;
      redirectPath = `/${defaultLang}${restOfPath}${location.search}${location.hash}`;
    }

    return <Navigate to={redirectPath} replace />;
  }

  // Synchronise i18next language with the URL parameter.
  // This effect runs whenever the lang parameter changes (e.g., when the
  // user navigates from /fr/... to /en/...).
  useEffect(() => {
    if (lang && isSupportedLanguage(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }

    // Also persist the choice in localStorage so it survives page refreshes
    try {
      localStorage.setItem("katalog-language", lang);
    } catch {
      // Ignore storage errors
    }
  }, [lang, i18n]);

  // Update the <html> lang attribute for accessibility and SEO
  useEffect(() => {
    if (lang && isSupportedLanguage(lang)) {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  // Render the child routes
  return <Outlet />;
}
